import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../base/context/MessageProvider";
import { SettingApiService } from "../../../service/seller/settingApiService";
import {
  FaShoppingCart, FaRupeeSign, FaCalendarDay, FaWallet,
  FaClock, FaBox, FaCrown, FaChartLine, FaChartBar, FaChartPie
} from "react-icons/fa";
import Chart from "react-apexcharts";
import "./sellerDashboard.css";

export default function SellerDashboardNew() {
  const navigate = useNavigate();
  const loader = useLoader();
  const message = useMessage();

  const [data, setData] = useState({
    walletBalance: 0,
    membershipPlan: "",
    totalOrders: 0,
    totalSales: 0,
    todayOrders: 0,
    todaySales: 0,
    pendingOrders: 0,
    subscribeProduct: 0,
    topSellingSKUs: null,
    topSellingCategories: null,
  });

  const fetchData = async () => {
    loader.showLoading(true);
    try {
      const res = await SettingApiService.getDashboard();
      loader.showLoading(false);
      if (res.status.code === 200) {
        setData(res.data);
      } else {
        message.showToast(res.status.message, ToastTypes.ERROR);
      }
    } catch (err) {
      loader.showLoading(false);
      message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatCurrency = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(2)}Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(2)}L`;
    if (value >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
    return `₹${value?.toFixed(2) || 0}`;
  };

  const formatNumber = (value) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value || 0;
  };

  const statsCards = [
    {
      title: "Total Orders",
      value: formatNumber(data.totalOrders),
      icon: FaShoppingCart,
      color: "#8b5cf6",
      bgColor: "rgba(139, 92, 246, 0.1)",
      link: "/seller/orders"
    },
    {
      title: "Total Sales",
      value: formatCurrency(data.totalSales),
      icon: FaRupeeSign,
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.1)",
      link: "/seller/wallet"
    },
    {
      title: "Today's Orders",
      value: formatNumber(data.todayOrders),
      icon: FaCalendarDay,
      color: "#3DD0E5",
      bgColor: "rgba(61, 208, 229, 0.1)",
      link: "/seller/orders?startDate=true&endDate=true"
    },
    {
      title: "Today's Sales",
      value: formatCurrency(data.todaySales),
      icon: FaChartLine,
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
      link: "/seller/wallet?startDate=true&endDate=true"
    },
    {
      title: "IM Cash Balance",
      value: formatCurrency(data.walletBalance),
      icon: FaWallet,
      color: "#ec4899",
      bgColor: "rgba(236, 72, 153, 0.1)",
      link: "/seller/wallet"
    },
    {
      title: "Pending Orders",
      value: formatNumber(data.pendingOrders),
      icon: FaClock,
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.1)",
      link: "/seller/orders?status=payment pending"
    },
    {
      title: "Subscribed Products",
      value: formatNumber(data.subscribeProduct),
      icon: FaBox,
      color: "#6366f1",
      bgColor: "rgba(99, 102, 241, 0.1)",
      link: "/seller/productSubscription"
    },
    {
      title: "Membership",
      value: data.membershipPlan || "Free",
      icon: FaCrown,
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
      link: "/seller/wallet"
    }
  ];

  // Bar Chart for Top Selling SKUs - Advanced Horizontal Bar
  const skuData = Array.isArray(data.topSellingSKUs) ? data.topSellingSKUs : [];
  const categoryData = Array.isArray(data.topSellingCategories) ? data.topSellingCategories : [];

  const skuChartOptions = {
    chart: {
      type: 'bar',
      toolbar: { show: false },
      fontFamily: "'Segoe UI', 'Roboto', sans-serif",
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 1000,
        animateGradually: { enabled: true, delay: 100 },
        dynamicAnimation: { enabled: true, speed: 400 }
      },
      sparkline: { enabled: false }
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: '60%',
        distributed: true,
        dataLabels: { 
          position: 'center',
        },
      }
    },
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'horizontal',
        shadeIntensity: 0.2,
        gradientToColors: ['#60e6f7', '#c4b5fd', '#6ee7b7', '#fde047', '#fca5a5', '#f9a8d4', '#93c5fd', '#a5b4fc', '#fcd34d', '#fda4af'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
        stops: [0, 100]
      }
    },
    colors: ['#3DD0E5', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#3b82f6', '#6366f1', '#eab308', '#f43f5e'],
    dataLabels: {
      enabled: true,
      textAnchor: 'start',
      formatter: (val, { dataPointIndex }) => {
        const totalAmount = skuData[dataPointIndex]?.totalAmount || 0;
        const formattedAmount = totalAmount >= 100000 
          ? `₹${(totalAmount/100000).toFixed(1)}L` 
          : totalAmount >= 1000 
            ? `₹${(totalAmount/1000).toFixed(1)}K` 
            : `₹${Math.round(totalAmount)}`;
        return `${val} | ${formattedAmount}`;
      },
      offsetX: 5,
      style: { 
        fontSize: '10px', 
        fontWeight: 600, 
        colors: ['#374151']
      },
      dropShadow: { enabled: false },
      background: { 
        enabled: true,
        foreColor: '#fff',
        padding: 4,
        borderRadius: 3,
        borderWidth: 0,
        opacity: 0.9
      }
    },
    legend: { show: false },
    xaxis: {
      categories: skuData.map(item => item.sku || ''),
      labels: {
        style: { fontSize: '11px', colors: '#9ca3af', fontWeight: 500 },
        formatter: (val) => Math.round(val),
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { 
        style: { fontSize: '12px', colors: '#374151', fontWeight: 600 },
        maxWidth: 160,
        formatter: (val) => {
          if (typeof val === 'string' && val.length > 20) {
            return val.substring(0, 20) + '...';
          }
          return val;
        }
      },
    },
    grid: {
      borderColor: '#f3f4f6',
      strokeDashArray: 0,
      padding: { left: 10, right: 20, top: -5, bottom: -5 },
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: false } }
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      style: { fontSize: '13px' },
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const sku = skuData[dataPointIndex]?.sku || '';
        const quantity = skuData[dataPointIndex]?.quantity || 0;
        const totalAmount = skuData[dataPointIndex]?.totalAmount || 0;
        const formattedAmount = totalAmount >= 100000 
          ? `₹${(totalAmount/100000).toFixed(2)}L` 
          : totalAmount >= 1000 
            ? `₹${(totalAmount/1000).toFixed(2)}K` 
            : `₹${totalAmount.toLocaleString()}`;
        return `<div style="padding: 10px 14px; background: #1f2937; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
          <div style="font-weight: 600; color: #fff; margin-bottom: 8px; font-size: 13px;">${sku}</div>
          <div style="color: #9ca3af; font-size: 12px; margin-bottom: 4px;">
            <span style="color: #3DD0E5;">●</span> Units Sold: <span style="color: #fff; font-weight: 600;">${quantity.toLocaleString()}</span>
          </div>
          <div style="color: #9ca3af; font-size: 12px;">
            <span style="color: #10b981;">●</span> Total Amount: <span style="color: #10b981; font-weight: 600;">${formattedAmount}</span>
          </div>
        </div>`;
      }
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.85 } },
      active: { filter: { type: 'darken', value: 0.8 } }
    }
  };

  const skuChartSeries = [{
    name: 'Quantity',
    data: skuData.map(item => item.quantity || 0)
  }];

  // Donut Chart for Top Selling Categories - Advanced
  const categoryChartOptions = {
    chart: {
      type: 'donut',
      fontFamily: "'Segoe UI', 'Roboto', sans-serif",
      background: 'transparent',
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 800,
        animateGradually: { enabled: true, delay: 150 },
        dynamicAnimation: { enabled: true, speed: 350 }
      },
      dropShadow: {
        enabled: true,
        top: 3,
        left: 0,
        blur: 8,
        opacity: 0.2
      }
    },
    labels: categoryData.map(item => {
      const cat = item.category || 'Unknown';
      return cat.length > 18 ? cat.substring(0, 18) + '...' : cat;
    }),
    colors: ['#3DD0E5', '#8b5cf6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'],
    fill: {
      type: 'gradient',
      gradient: {
        shade: 'dark',
        type: 'diagonal1',
        shadeIntensity: 0.3,
        gradientToColors: ['#60e6f7', '#a78bfa', '#34d399', '#fbbf24', '#f87171', '#f472b6'],
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 0.9,
        stops: [0, 100]
      }
    },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 270,
        expandOnClick: true,
        donut: {
          size: '65%',
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '16px',
              fontWeight: 600,
              color: '#374151',
              offsetY: -12,
              formatter: (val) => val
            },
            value: {
              show: true,
              fontSize: '28px',
              fontWeight: 700,
              color: '#111827',
              offsetY: 8,
              formatter: (val) => val
            },
            total: {
              show: true,
              showAlways: true,
              label: 'Total Units',
              fontSize: '13px',
              fontWeight: 500,
              color: '#6b7280',
              formatter: (w) => {
                const total = w.globals.seriesTotals.reduce((a, b) => a + b, 0);
                return total.toLocaleString();
              }
            }
          }
        }
      }
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: true,
      position: 'bottom',
      fontSize: '13px',
      fontWeight: 500,
      markers: { width: 14, height: 14, radius: 4, offsetX: -4 },
      itemMargin: { horizontal: 12, vertical: 8 },
      formatter: (seriesName, opts) => {
        const val = opts.w.globals.series[opts.seriesIndex];
        return `${seriesName}: ${val}`;
      }
    },
    tooltip: {
      enabled: true,
      theme: 'dark',
      style: { fontSize: '13px' },
      custom: function({ series, seriesIndex, w }) {
        const category = categoryData[seriesIndex]?.category || 'Unknown';
        const quantity = categoryData[seriesIndex]?.quantity || 0;
        const totalAmount = categoryData[seriesIndex]?.totalAmount || 0;
        const formattedAmount = totalAmount >= 100000 
          ? `₹${(totalAmount/100000).toFixed(2)}L` 
          : totalAmount >= 1000 
            ? `₹${(totalAmount/1000).toFixed(2)}K` 
            : `₹${totalAmount.toLocaleString()}`;
        return `<div style="padding: 10px 14px; background: #1f2937; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">
          <div style="font-weight: 600; color: #fff; margin-bottom: 8px; font-size: 13px;">${category}</div>
          <div style="color: #9ca3af; font-size: 12px; margin-bottom: 4px;">
            <span style="color: #3DD0E5;">●</span> Units Sold: <span style="color: #fff; font-weight: 600;">${quantity.toLocaleString()}</span>
          </div>
          <div style="color: #9ca3af; font-size: 12px;">
            <span style="color: #10b981;">●</span> Total Amount: <span style="color: #10b981; font-weight: 600;">${formattedAmount}</span>
          </div>
        </div>`;
      },
      fillSeriesColor: false
    },
    stroke: {
      width: 3,
      colors: ['#ffffff']
    },
    states: {
      hover: { filter: { type: 'darken', value: 0.9 } },
      active: { allowMultipleDataPointsSelection: false, filter: { type: 'darken', value: 0.9 } }
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: { height: 280 },
        legend: { position: 'bottom', fontSize: '11px' }
      }
    }]
  };

  const categoryChartSeries = categoryData.map(item => item.quantity || 0);

  return (
    <div className="seller-dash-container">
      {/* Stats Cards Grid */}
      <div className="seller-dash-stats-grid">
        {statsCards.map((card, index) => (
          <div
            key={index}
            className="seller-dash-stat-card"
            onClick={() => navigate(card.link)}
          >
            <div className="seller-dash-stat-icon" style={{ background: card.bgColor }}>
              <card.icon style={{ color: card.color }} />
            </div>
            <div className="seller-dash-stat-content">
              <span className="seller-dash-stat-title">{card.title}</span>
              <span className="seller-dash-stat-value" style={{ color: card.color }}>
                {card.value}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="seller-dash-charts-grid">
        {/* Top Selling SKUs - Bar Chart */}
        <div className="seller-dash-chart-card">
          <div className="seller-dash-chart-header">
            <div className="seller-dash-chart-title">
              <FaChartBar className="seller-dash-chart-icon" />
              <span>Top Selling SKUs</span>
            </div>
          </div>
          <div className="seller-dash-chart-body">
            {skuData.length > 0 ? (
              <Chart
                options={skuChartOptions}
                series={skuChartSeries}
                type="bar"
                height={skuData.length > 5 ? 400 : 320}
                width="100%"
              />
            ) : (
              <div className="seller-dash-no-data">No SKU data available</div>
            )}
          </div>
        </div>

        {/* Top Selling Categories - Donut Chart */}
        <div className="seller-dash-chart-card">
          <div className="seller-dash-chart-header">
            <div className="seller-dash-chart-title">
              <FaChartPie className="seller-dash-chart-icon" />
              <span>Top Selling Categories</span>
            </div>
          </div>
          <div className="seller-dash-chart-body">
            {categoryData.length > 0 ? (
              <Chart
                options={categoryChartOptions}
                series={categoryChartSeries}
                type="donut"
                height={300}
                width="100%"
              />
            ) : (
              <div className="seller-dash-no-data">No category data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="seller-dash-quick-actions">
        <h3 className="seller-dash-section-title">Quick Actions</h3>
        <div className="seller-dash-actions-grid">
          <button className="seller-dash-action-btn" onClick={() => navigate('/seller/orders')}>
            <FaShoppingCart />
            <span>View Orders</span>
          </button>
          <button className="seller-dash-action-btn" onClick={() => navigate('/seller/productSubscription')}>
            <FaBox />
            <span>Products</span>
          </button>
          <button className="seller-dash-action-btn" onClick={() => navigate('/seller/wallet')}>
            <FaWallet />
            <span>IM Cash</span>
          </button>
          <button className="seller-dash-action-btn" onClick={() => navigate('/seller/reports/invoice')}>
            <FaChartLine />
            <span>Invoices</span>
          </button>
        </div>
      </div>
    </div>
  );
}
